import { useMemo } from "react";
import { useSelector } from "react-redux";

import { UseQueryResult } from "@tanstack/react-query";

import { OrderSet } from "@models/OrderSet";

import { useDraftOrderSetsQuery, useOrderSetQuery } from "./data";

type DraftOrderSetId = {
  id: string | undefined;
  isFetching: boolean;
};

function useGetDraftOrderSetId(orderType: "inventory" | "on-demand"): DraftOrderSetId; // prettier-ignore
function useGetDraftOrderSetId(orderType: "pre-order",  programId: string, orderWindowId: string | undefined): DraftOrderSetId; // prettier-ignore
function useGetDraftOrderSetId(
  orderType: "inventory" | "on-demand" | "pre-order",
  programId?: string,
  orderWindowId?: string
): DraftOrderSetId {
  const currentChannelId: string | null = useSelector(
    (state: any) => state.currentUser.currentChannelId
  );
  const currentTerritoryId: string | null = useSelector(
    (state: any) => state.currentUser.currentTerritoryId
  );
  const { isFetching, data } = useDraftOrderSetsQuery();

  const orderSet = useMemo(
    () =>
      data.find(
        (os) =>
          os.type === orderType &&
          (os.territoryId ?? null) === currentTerritoryId &&
          (os.channel?.id ?? null) === currentChannelId &&
          (orderType === "pre-order"
            ? os.program?.id === programId &&
              os.orderWindow?.id === orderWindowId
            : true)
      ),
    [
      data,
      orderType,
      currentChannelId,
      currentTerritoryId,
      programId,
      orderWindowId,
    ]
  );

  return { id: orderSet?.id, isFetching };
}

type OrderSetQueryReturnType = UseQueryResult<OrderSet, Error>;

function useDraftOrderSetQuery(orderType: "inventory" | "on-demand"): OrderSetQueryReturnType; // prettier-ignore
function useDraftOrderSetQuery(orderType: "pre-order",  programId: string, orderWindowId: string | undefined): OrderSetQueryReturnType; // prettier-ignore
function useDraftOrderSetQuery(
  orderType: "inventory" | "on-demand" | "pre-order",
  programId?: string,
  orderWindowId?: string
): OrderSetQueryReturnType {
  const { id, isFetching } = useGetDraftOrderSetId(
    orderType as any,
    programId as any,
    orderWindowId
  );

  const orderSetQuery = useOrderSetQuery(id);
  orderSetQuery.isFetching ||= isFetching;

  return orderSetQuery;
}

export { useGetDraftOrderSetId };
export default useDraftOrderSetQuery;
